import { addLocaleData } from 'react-intl';
import { getItem, setItem } from './local-storage'

export const DEFAULT_LOCALE = 'en';
export const LOCAL_STORAGE_LOCALE = 'locale';

export const LOCALES = {
  en: 'English',
  cs: 'Čeština',
  da: 'Dansk',
  de: 'Deutsch',
  fr: 'Français',
  es: 'Español',
  'es-mx': 'Español (Mexico)',
  nl: 'Nederlands',
  'nl-be': 'Nederlands (België)',
  nb: 'Norsk Bokmål',
  pl: 'Polski',
  fi: 'Suomen kieli',
  sv: 'Svenska'
};

export const localesNavBar = {
  en: 'en',
  cs: 'cs',
  da: 'da',
  de: 'de',
  es: 'es',
  'es-mx': 'es-mx',
  fr: 'fr',
  nl: 'nl',
  'nl-be': 'nl-be',
  nb: 'nb',
  pl: 'pl',
  fi: 'fi',
  sv: 'sv',
}

const supportedLocale = locale => {
  if (!locale) return null;

  const localeParts = locale.split('-');
  let formattedLocale;

  if (localeParts.length === 2) {
    formattedLocale = `${localeParts[0].toLowerCase()}-${localeParts[1].toLowerCase()}`;
  } else {
    formattedLocale = localeParts[0].toLowerCase();
  }

  return LOCALES[formattedLocale] && formattedLocale;
};

const localeFromUrl = () => {
  const { searchParams } = new URL(window.location);
  const locale = decodeURIComponent(searchParams.get('l'));
  return supportedLocale(locale);
};

export const getLocale = () => supportedLocale(getItem(LOCAL_STORAGE_LOCALE)) || DEFAULT_LOCALE

// The `locale` key is shared with every other Universe app on this origin, and
// enterprise treats whatever it reads there as a deliberate language choice — an
// unrecognised value is coerced to English and written back to the user's account.
// So refuse to publish anything we cannot name, and publish the normalized form so
// listeners in other tabs always match on it (UNI-2795).
export const setLocale = locale => {
  if (typeof locale !== 'string') return undefined;

  const normalizedLocale = supportedLocale(locale);
  if (!normalizedLocale) return undefined;

  return setItem(LOCAL_STORAGE_LOCALE, normalizedLocale);
}

export const detectInitialLocale = () => localeFromUrl() || getLocale() || DEFAULT_LOCALE;

export const loadMessages = async locale => {
  let localeDataPromise;
  let messagesPromise;

  switch (locale) {
    case 'cs':
      localeDataPromise = import(/* webpackChunkName: "react-intl-sv" */ 'react-intl/locale-data/cs')
      messagesPromise = import(/* webpackChunkName: "locales-sv" */ '../translations/locales/cs.json')
      break
    case 'da':
      localeDataPromise = import(/* webpackMode: "lazy" */ 'react-intl/locale-data/da');
      messagesPromise = import(/* webpackMode: "lazy" */ '../translations/locales/da.json');
      break;
    case 'de':
      localeDataPromise = import(/* webpackMode: "lazy" */ 'react-intl/locale-data/de');
      messagesPromise = import(/* webpackMode: "lazy" */ '../translations/locales/de.json');
      break;
    case 'es':
      localeDataPromise = import(/* webpackMode: "lazy" */ 'react-intl/locale-data/es');
      messagesPromise = import(/* webpackMode: "lazy" */ '../translations/locales/es.json');
      break;
    case 'es-mx':
      localeDataPromise = import(/* webpackChunkName: "react-intl-sv" */ 'react-intl/locale-data/es')
      messagesPromise = import(/* webpackChunkName: "locales-sv" */ '../translations/locales/es-mx.json')
      break
    case 'fi':
      localeDataPromise = import(/* webpackMode: "lazy" */ 'react-intl/locale-data/fi');
      messagesPromise = import(/* webpackMode: "lazy" */ '../translations/locales/fi.json');
      break;
    case 'fr':
      localeDataPromise = import(/* webpackMode: "lazy" */ 'react-intl/locale-data/fr');
      messagesPromise = import(/* webpackMode: "lazy" */ '../translations/locales/fr.json');
      break;
    case 'nl':
      localeDataPromise = import(/* webpackMode: "lazy" */ 'react-intl/locale-data/nl');
      messagesPromise = import(/* webpackMode: "lazy" */ '../translations/locales/nl.json');
      break;
    case 'nl-be':
      localeDataPromise = import(/* webpackMode: "lazy" */ 'react-intl/locale-data/nl');
      messagesPromise = import(/* webpackMode: "lazy" */ '../translations/locales/nl-be.json');
      break;
    case 'nb':
      localeDataPromise = import(/* webpackMode: "lazy" */ 'react-intl/locale-data/nb');
      messagesPromise = import(/* webpackMode: "lazy" */ '../translations/locales/nb.json');
      break;
    case 'pl':
      localeDataPromise = import(/* webpackChunkName: "react-intl-sv" */ 'react-intl/locale-data/pl')
      messagesPromise = import(/* webpackChunkName: "locales-sv" */ '../translations/locales/pl.json')
      break
    case 'sv':
      localeDataPromise = import(/* webpackMode: "lazy" */ 'react-intl/locale-data/sv');
      messagesPromise = import(/* webpackMode: "lazy" */ '../translations/locales/sv.json');
      break;
    default:
      localeDataPromise = import(/* webpackMode: "lazy" */ 'react-intl/locale-data/en');
      messagesPromise = import(/* webpackMode: "lazy" */ '../translations/locales/en.json');
  }

  // https://github.com/webpack/webpack/issues/7792#issuecomment-408093411
  const localeData = (await localeDataPromise).default;
  addLocaleData(localeData);

  const messages = await messagesPromise;
  return messages;
};
